<template>
  <div class="phrase-library-container">
    <div class="page-header">
      <h1>Phrase Library</h1>
      <button class="edit-data-btn" @click="handleEditData">
        <i class="fas fa-edit"></i>
        Edit Data
      </button>
    </div>
    
    <div class="content-area">
      <!-- Role Instances Table -->
      <div class="table-section">
        <h2>Instance Mappings</h2>
        <div v-if="isLoadingTable || isLoadingLinks || isLoadingInstances || isLoadingCategories" class="loading-message">
          <i class="fas fa-spinner fa-spin"></i> Loading data...
        </div>
        <div v-else-if="!uniqueRoleInstances || uniqueRoleInstances.length === 0" class="empty-message">
          No instance mappings found.
        </div>
        <div v-else class="table-container">
          <table class="role-instances-table">
            <thead>
              <tr>
                <th>Clinic Name</th>
                <th>Default Instance</th>
                <th>Category</th>
                <th>Role</th>
                <th>Changed Instance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(mapping, index) in uniqueRoleInstances" :key="index">
                <td>{{ getClinicName(mapping.clinicLinkId) }}</td>
                <td>{{ getDefaultInstanceLabel(mapping.clinicLinkId) }}</td>
                <td>{{ getCategoryName(mapping.categoryId) }}</td>
                <td>{{ getRoleName(mapping.categoryId, mapping.roleId) }}</td>
                <td>
                  <span v-if="mapping.instanceId && mapping.instanceId !== mapping.clinicLinkId" class="changed-instance">
                    {{ getInstanceLabel(mapping.instanceId) }}
                  </span>
                  <span v-else class="default-label">Default</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Mapping Editor Modal -->
    <div v-if="showMappingModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal mapping-modal">
        <!-- Sticky Header -->
        <div class="modal-header">
          <h2>Edit Instance Mapping</h2>
          <button class="modal-close-btn" @click="closeModal">✕</button>
        </div>
        
        <!-- Scrollable Content -->
        <div class="modal-content">
          <!-- Loading State -->
          <div v-if="isLoadingInstances" class="loading-message">
            <i class="fas fa-spinner fa-spin"></i> Loading instances...
          </div>
          
          <!-- Clinic Dropdown -->
          <div class="form-group">
            <label>Select Clinic:</label>
            <div class="clinic-dropdown" ref="clinicDropdown">
              <div class="clinic-selected" @click.stop="toggleClinicDropdown">
                {{ selectedClinic ? (selectedClinic.clinicName || selectedClinic.name) : "Select Clinic" }}
                <i :class="clinicDropdownOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
              </div>
              <div v-if="clinicDropdownOpen" class="clinic-options" @click.stop>
                <div class="clinic-search-container">
                  <input
                    type="text"
                    v-model="clinicSearchQuery"
                    :key="`clinic-search-${clinicDropdownOpen}`"
                    placeholder="Search clinic... (case-insensitive)"
                    class="clinic-search-input"
                    ref="clinicSearchInput"
                    autocomplete="off"
                    spellcheck="false"
                  />
                  <i class="fas fa-search clinic-search-icon"></i>
                </div>
                <div v-if="filteredClinics.length === 0 && clinicSearchQuery" class="clinic-no-results">
                  No clinics found
                </div>
                <div
                  v-for="clinic in filteredClinics"
                  :key="clinic.clinicLinkId"
                  class="clinic-option"
                  @click.stop="selectClinic(clinic)"
                >
                  {{ clinic.clinicName || clinic.name }}
                </div>
              </div>
            </div>
          </div>

          <!-- Default Instance -->
          <div v-if="selectedClinic" class="form-group">
            <label>Default Instance:</label>
            <div class="default-instance-display">
              {{ defaultInstanceName }}
            </div>
          </div>

          <!-- Instances Dropdown (for changing all) -->
          <div v-if="selectedClinic && !isLoadingInstances" class="form-group">
            <label>Change Instance (All):</label>
            <select v-model="selectedDefaultInstance" @change="onDefaultInstanceChange" class="instance-select">
              <option :value="null">Select Instance</option>
              <option :value="defaultInstanceId">{{ defaultInstanceName }} (Default)</option>
              <option
                v-for="instanceId in instances"
                :key="instanceId"
                :value="instanceId"
              >
                {{ instancesMap[instanceId] || instanceId }}
              </option>
            </select>
          </div>

          <!-- Categories and Roles Section -->
        <div v-if="selectedClinic && categories && categories.length > 0 && !isLoadingInstances" class="categories-section">
            <h3>Categories & Roles</h3>
            
            <div
              v-for="category in categories"
              :key="category.id"
              class="category-item"
            >
              <div class="category-header">
                <span class="category-name">{{ category.name }}</span>
                <select
                  v-model="selectedCategoryInstances[category.id]"
                  @change="onCategoryInstanceChange($event, category)"
                  class="instance-select category-instance"
                  ref="categoryInstanceSelect"
                >
                  <option :value="null">Select Instance</option>
                  <option :value="defaultInstanceId">{{ defaultInstanceName }} (Default)</option>
                  <option
                    v-for="instanceId in instances"
                    :key="instanceId"
                    :value="instanceId"
                  >
                    {{ instancesMap[instanceId] || instanceId }}
                  </option>
                </select>
              </div>

              <div class="roles-list">
                <div
                  v-for="role in category.roles || []"
                  :key="role.id"
                  class="role-item"
                >
                  <span class="role-name">{{ role.name }}</span>
                  <select
                    :value="getRoleInstance(category.id, role.id)"
                    @change="onRoleInstanceChange($event, category, role)"
                    :class="['instance-select role-instance', { 'highlighted': !isDefaultInstance(getRoleInstance(category.id, role.id)) }]"
                  >
                    <option :value="defaultInstanceId">{{ defaultInstanceName }} (Default)</option>
                    <option
                      v-for="instance in instances"
                      :key="instance"
                      :value="instance"
                    >
                      {{ instance }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sticky Actions -->
        <div class="modal-actions">
          <button type="button" class="cancel-btn" @click="closeModal">Cancel</button>
          <button
            type="button"
            class="save-btn"
            @click="saveMapping"
            :disabled="isSaving || !selectedClinic"
          >
            {{ isSaving ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showConfirmationModal" class="modal-overlay" @click.self="cancelConfirmation">
      <div class="modal confirmation-modal">
        <h3>Confirm Change</h3>
        <p>{{ confirmationMessage }}</p>
        <div class="confirmation-actions">
          
          <button
            v-if="confirmationType === 'defaultInstance'"
            @click="confirmChangeAllRoles"
            class="confirm-btn"
          >
            Change All Roles
          </button>
          <button
            v-if="confirmationType === 'category'"
            @click="confirmChangeCategoryAllRoles"
            class="confirm-btn"
          >
            Change All Roles of this Category
          </button>
          <button @click="cancelConfirmation" class="cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "PhraseLibraryView",
  data() {
    return {
      showMappingModal: false,
      selectedClinic: null,
      clinicDropdownOpen: false,
      instances: [], // Array of instance IDs from /api/instances?tag=start
      instancesMap: {}, // Map of instanceId -> instance name (from links)
      linksMap: {}, // Map of link.id -> link.instance for default instances
      categories: [],
      roleInstanceMap: {}, // { categoryId-roleId: instanceId }
      originalRoleInstanceMap: {}, // Original state when clinic was selected
      roleInstanceIdMap: {}, // { categoryId-roleId: id } - stores the id for updates
      defaultInstanceId: null,
      defaultInstanceName: "",
      selectedDefaultInstance: null,
      selectedCategoryInstances: {}, // { categoryId: selectedInstanceId }
      clinicSearchQuery: "",
      isSaving: false,
      isLoadingInstances: false,
      isLoadingTable: false,
      isLoadingLinks: false,
      isLoadingCategories: false,
      allRoleInstances: [], // All role instances from API
      showConfirmationModal: false,
      confirmationType: null, // 'defaultInstance' | 'category'
      confirmationMessage: "",
      pendingChange: null,
    };
  },
  computed: {
    ...mapGetters(["getClinics"]),
    clinics() {
      return this.getClinics || [];
    },
    storeCategories() {
      return this.$store.state.categories || [];
    },
    uniqueRoleInstances() {
      // Get all clinics and categories
      const clinicsList = this.clinics || [];
      const categoriesList = this.categories || [];
      
      // If no clinics or categories, return empty
      if (clinicsList.length === 0 || categoriesList.length === 0) {
        return [];
      }
      
      // Create a map of role instances by clinic-category-role key
      const roleInstanceMap = new Map();
      if (this.allRoleInstances && Array.isArray(this.allRoleInstances)) {
        this.allRoleInstances.forEach(mapping => {
          const key = `${mapping.clinicLinkId}-${mapping.categoryId}-${mapping.roleId}`;
          roleInstanceMap.set(key, mapping);
        });
      }
      
      // Create a map to track unique combinations we've added
      const seen = new Map();
      const unique = [];
      
      // For each clinic, show all category-role combinations
      clinicsList.forEach(clinic => {
        categoriesList.forEach(category => {
          if (category && category.roles && Array.isArray(category.roles)) {
            category.roles.forEach(role => {
              const key = `${clinic.clinicLinkId}-${category.id}-${role.id}`;
              
              // Only add if we haven't seen this combination before
              if (!seen.has(key)) {
                seen.set(key, true);
                
                // Get role instance data if it exists
                const roleInstance = roleInstanceMap.get(key);
                if (roleInstance) {
                  unique.push(roleInstance);
                }
                // Don't add default entries - only show changed instances
              }
            });
          }
        });
      });
      
      // Filter out default instances (where instanceId is null or equals clinicLinkId)
      const filtered = unique.filter(mapping => {
        // Only include if instanceId exists and is different from clinicLinkId
        return mapping.instanceId && mapping.instanceId !== mapping.clinicLinkId;
      });
      
      // Reverse the array to show rows in reverse order
      return filtered.reverse();
    },
    filteredClinics() {
      // Explicitly access reactive properties directly to ensure Vue tracks dependencies
      // Access clinics to establish dependency
      const clinicsList = Array.isArray(this.clinics) ? this.clinics : [];
      
      // Access clinicSearchQuery directly (don't use intermediate variable to preserve reactivity)
      const rawQuery = this.clinicSearchQuery;
      const searchQuery = rawQuery ? String(rawQuery).trim() : "";
      
      // If no search query, return all clinics sorted alphabetically
      if (!searchQuery) {
        return [...clinicsList].sort((a, b) => {
          const nameA = String(a?.clinicName || a?.name || "").toLowerCase();
          const nameB = String(b?.clinicName || b?.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }
      
      const query = searchQuery.toLowerCase();
      if (!query) {
        return [...clinicsList].sort((a, b) => {
          const nameA = String(a?.clinicName || a?.name || "").toLowerCase();
          const nameB = String(b?.clinicName || b?.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }
      
      // Filter clinics that match the query (case-insensitive)
      // Normalize clinic names: handle both clinicName and name properties
      const filtered = clinicsList.filter(clinic => {
        if (!clinic) return false;
        // Get clinic name (try clinicName first, then name)
        const clinicName = clinic.clinicName || clinic.name || "";
        if (!clinicName) return false;
        // Convert to lowercase for case-insensitive comparison
        const normalizedName = clinicName.toLowerCase().trim();
        // Check if query is included (case-insensitive)
        return normalizedName.includes(query);
      });
      
      // Sort results: exact matches first (alphabetically), then starts with query (alphabetically), then contains query (alphabetically)
      return filtered.sort((a, b) => {
        const nameA = (a?.clinicName || a?.name || "").toLowerCase();
        const nameB = (b?.clinicName || b?.name || "").toLowerCase();
        
        // Exact match gets highest priority
        const aExact = nameA === query;
        const bExact = nameB === query;
        if (aExact && !bExact) return -1;
        if (bExact && !aExact) return 1;
        // If both are exact matches, sort alphabetically
        if (aExact && bExact) {
          return nameA.localeCompare(nameB);
        }
        
        // Starts with query gets second priority
        const aStartsWith = nameA.startsWith(query);
        const bStartsWith = nameB.startsWith(query);
        if (aStartsWith && !bStartsWith) return -1;
        if (bStartsWith && !aStartsWith) return 1;
        // If both start with query, sort alphabetically
        if (aStartsWith && bStartsWith) {
          return nameA.localeCompare(nameB);
        }
        
        // Otherwise, both contain query but don't start with it - sort alphabetically
        return nameA.localeCompare(nameB);
      });
    },
  },
  mounted() {
    // Call all APIs immediately without waiting for anything
    this.loadInstances();
    this.loadLinks(); // Load links for default instance mapping
    this.loadAllRoleInstances();
    this.loadCategories();
    
    // Fetch clinics in parallel
    this.$store.dispatch("fetchClinics");
    
    // Add click outside handler
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  watch: {
    storeCategories: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.categories = newVal;
        }
      }
    }
  },
  methods: {
    ...mapActions(["fetchRoleInstances", "saveInstanceMap", "fetchInstances", "fetchAllRoleInstances", "fetchClinics"]),
    
    async loadInstances() {
      if (this.instances && this.instances.length > 0) {
        // Already loaded, no need to fetch again
        return;
      }
      
      this.isLoadingInstances = true;
      try {
        // Fetch instance IDs from /api/instances?tag=start
        // Returns array: [instanceid1, instanceid2, ...]
        const instanceIds = await this.$store.dispatch("fetchInstances");
        this.instances = Array.isArray(instanceIds) ? instanceIds : [];
      } catch (error) {
        console.error("Error loading instances:", error);
        this.instances = [];
      } finally {
        this.isLoadingInstances = false;
      }
    },

    async loadLinks() {
      this.isLoadingLinks = true;
      try {
        const links = await this.$store.dispatch("fetchLinks");
        // Create mapping: link.id -> link.instance for default instances
        this.linksMap = {};
        // Also populate instancesMap with instance names from links
        if (!this.instancesMap) {
          this.instancesMap = {};
        }
        if (Array.isArray(links)) {
          links.forEach(link => {
            if (link && link.id && link.instance) {
              this.linksMap[link.id] = link.instance;
              // Store instance name for lookup
              if (link.name) {
                this.instancesMap[link.instance] = link.name;
              }
            }
          });
        }
      } catch (error) {
        console.error("Error loading links:", error);
        this.linksMap = {};
      } finally {
        this.isLoadingLinks = false;
      }
    },

    async loadAllRoleInstances() {
      this.isLoadingTable = true;
      try {
        const data = await this.$store.dispatch("fetchAllRoleInstances");
        this.allRoleInstances = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error loading all role instances:", error);
        this.allRoleInstances = [];
      } finally {
        this.isLoadingTable = false;
      }
    },

    async loadCategories() {
      this.isLoadingCategories = true;
      try {
        await this.$store.dispatch("fetchCategories");
        this.categories = this.$store.state.categories || [];
      } catch (error) {
        console.error("Error loading categories:", error);
        this.categories = [];
      } finally {
        this.isLoadingCategories = false;
      }
    },

    getClinicName(clinicLinkId) {
      const clinic = this.clinics.find(c => c.clinicLinkId === clinicLinkId);
      return clinic ? (clinic.clinicName || clinic.name) : clinicLinkId;
    },

    getDefaultInstanceLabel(clinicLinkId) {
      if (!clinicLinkId) return "Default";
      // Get the instance ID from linksMap using clinicLinkId
      const instanceId = this.linksMap[clinicLinkId];
      if (!instanceId) return clinicLinkId;
      // Show link.instance (instance ID) instead of link.name
      return instanceId;
    },

    getInstanceLabel(instanceId) {
      if (!instanceId) return "Default";
      // Check if it's a link ID, get the instance from linksMap
      if (this.linksMap[instanceId]) {
        const actualInstanceId = this.linksMap[instanceId];
        return this.instancesMap[actualInstanceId] || actualInstanceId;
      }
      // Otherwise, it's already an instance ID
      return this.instancesMap[instanceId] || instanceId;
    },

    getCategoryName(categoryId) {
      if (!this.categories || !Array.isArray(this.categories)) {
        return categoryId;
      }
      const category = this.categories.find(c => c && c.id === categoryId);
      return category ? category.name : categoryId;
    },

    getRoleName(categoryId, roleId) {
      if (!this.categories || !Array.isArray(this.categories)) {
        return roleId;
      }
      const category = this.categories.find(c => c && c.id === categoryId);
      if (category && category.roles && Array.isArray(category.roles)) {
        const role = category.roles.find(r => r && r.id === roleId);
        return role ? role.name : roleId;
      }
      return roleId;
    },

    async handleEditData() {
      // Load instances when opening the modal (links already loaded on mount)
      await this.loadInstances();
      this.showMappingModal = true;
      // Fetch clinics if not already loaded
      if (!this.clinics || this.clinics.length === 0) {
        this.$store.dispatch("fetchClinics");
      }
    },

    closeModal() {
      this.showMappingModal = false;
      this.selectedClinic = null;
      this.roleInstanceMap = {};
      this.originalRoleInstanceMap = {};
      this.roleInstanceIdMap = {};
      this.defaultInstanceId = null;
      this.defaultInstanceName = "";
      this.selectedDefaultInstance = null;
      this.selectedCategoryInstances = {};
      this.clinicDropdownOpen = false;
      this.clinicSearchQuery = "";
      // Note: We keep instances loaded for better UX if user reopens modal
    },

    toggleClinicDropdown() {
      this.clinicDropdownOpen = !this.clinicDropdownOpen;
      // Focus search input when dropdown opens
      if (this.clinicDropdownOpen) {
        this.$nextTick(() => {
          if (this.$refs.clinicSearchInput) {
            this.$refs.clinicSearchInput.focus();
            // Clear any previous search when reopening dropdown
            this.clinicSearchQuery = "";
          }
        });
      }
      // Don't clear search when closing - let user keep their search if they reopen
    },

    async selectClinic(clinic) {
      this.selectedClinic = clinic;
      this.clinicDropdownOpen = false;
      this.clinicSearchQuery = "";
      
      // Get default instance ID from linksMap using clinicLinkId
      const actualDefaultInstanceId = this.linksMap[clinic.clinicLinkId] || clinic.clinicLinkId;
      // Set default instance to the actual instance ID (not clinicLinkId)
      this.defaultInstanceId = actualDefaultInstanceId;
      // Show link.instance (instance ID) instead of link.name
      this.defaultInstanceName = actualDefaultInstanceId || (clinic.clinicName || clinic.name || clinic.clinicLinkId);
      this.selectedDefaultInstance = null;
      // Initialize all category dropdowns to null (show "Select Instance" placeholder)
      if (this.categories && Array.isArray(this.categories)) {
        this.categories.forEach(cat => {
          if (cat && cat.id) {
            this.selectedCategoryInstances[cat.id] = null;
          }
        });
      }

      try {
        // Initialize roleInstanceMap from /all API first
        this.roleInstanceMap = {};
        this.originalRoleInstanceMap = {};
        this.roleInstanceIdMap = {};
        
        // First, populate from allRoleInstances (/all API) if available
        if (Array.isArray(this.allRoleInstances)) {
          this.allRoleInstances.forEach(item => {
            if (item.clinicLinkId === clinic.clinicLinkId) {
              const key = `${item.categoryId}-${item.roleId}`;
              // Get instanceId from /all API
              let instanceId = item.instanceId || null;
              // If instanceId matches the default instance or clinicLinkId, store null (so it shows as default)
              // Otherwise, store the actual instanceId (changed instance)
              if (instanceId && (instanceId === actualDefaultInstanceId || instanceId === clinic.clinicLinkId)) {
                instanceId = null;
              }
              this.roleInstanceMap[key] = instanceId;
              this.originalRoleInstanceMap[key] = instanceId;
              // Store the id for updates
              if (item.id) {
                this.roleInstanceIdMap[key] = item.id;
              }
            }
          });
        }
        
        // Initialize all category-role combinations with default instance if not found in /all API
        if (this.categories && Array.isArray(this.categories)) {
          this.categories.forEach(category => {
            if (category && category.roles && Array.isArray(category.roles)) {
              category.roles.forEach(role => {
                const key = `${category.id}-${role.id}`;
                // If not found in /all API, use null (which will default to defaultInstanceId)
                if (!(key in this.roleInstanceMap)) {
                  this.roleInstanceMap[key] = null;
                  this.originalRoleInstanceMap[key] = null;
                }
              });
            }
          });
        }
        
        // Fetch role instances for this clinic to get IDs for updates (but don't overwrite values from /all API)
        const roleInstances = await this.$store.dispatch("fetchRoleInstances", clinic.clinicLinkId);
        if (Array.isArray(roleInstances)) {
          roleInstances.forEach(item => {
            const key = `${item.categoryId}-${item.roleId}`;
            // Only update the id map if not already set from /all API
            if (item.id && !this.roleInstanceIdMap[key]) {
              this.roleInstanceIdMap[key] = item.id;
            }
          });
        }
      } catch (error) {
        console.error("Error fetching role instances:", error);
        // If error occurs, still initialize with default values
        this.roleInstanceMap = {};
        this.originalRoleInstanceMap = {};
        if (this.categories && Array.isArray(this.categories)) {
          this.categories.forEach(category => {
            if (category && category.roles && Array.isArray(category.roles)) {
              category.roles.forEach(role => {
                const key = `${category.id}-${role.id}`;
                this.roleInstanceMap[key] = null;
                this.originalRoleInstanceMap[key] = null;
              });
            }
          });
        }
      }
    },

    getCategoryInstance(categoryId) {
      // Check if all roles in this category use the same instance
      if (!this.categories || !Array.isArray(this.categories)) {
        return this.defaultInstanceId;
      }
      const category = this.categories.find(c => c && c.id === categoryId);
      if (!category || !category.roles || !Array.isArray(category.roles) || category.roles.length === 0) {
        return this.defaultInstanceId;
      }

      const firstRoleInstance = this.getRoleInstance(categoryId, category.roles[0].id);
      const allSame = category.roles.every(role => {
        return this.getRoleInstance(categoryId, role.id) === firstRoleInstance;
      });

      return allSame ? firstRoleInstance : this.defaultInstanceId;
    },

    getRoleInstance(categoryId, roleId) {
      const key = `${categoryId}-${roleId}`;
      return this.roleInstanceMap[key] || this.defaultInstanceId;
    },

    isDefaultInstance(instanceId) {
      return !instanceId || instanceId === this.defaultInstanceId;
    },

    onDefaultInstanceChange() {
      if (!this.selectedDefaultInstance) return;
      
      this.confirmationType = "defaultInstance";
      this.confirmationMessage = "Do you want to change default instance for all roles?";
      this.pendingChange = {
        type: "defaultInstance",
        instanceId: this.selectedDefaultInstance
      };
      this.showConfirmationModal = true;
    },

    confirmChangeAllDefaultRoles() {
      // Change only roles that are currently using default instance
      if (!this.categories || !Array.isArray(this.categories)) {
        return;
      }
      this.categories.forEach(cat => {
        if (cat && cat.roles && Array.isArray(cat.roles)) {
          cat.roles.forEach(role => {
            const key = `${cat.id}-${role.id}`;
            if (!this.roleInstanceMap[key] || this.roleInstanceMap[key] === this.defaultInstanceId) {
              this.roleInstanceMap[key] = this.pendingChange.instanceId;
            }
          });
        }
      });
      this.selectedDefaultInstance = null;
      this.showConfirmationModal = false;
      this.pendingChange = null;
    },

    confirmChangeAllRoles() {
      // Change all roles regardless of current instance
      if (!this.categories || !Array.isArray(this.categories)) {
        return;
      }
      this.categories.forEach(cat => {
        if (cat && cat.roles && Array.isArray(cat.roles)) {
          cat.roles.forEach(role => {
            const key = `${cat.id}-${role.id}`;
            this.roleInstanceMap[key] = this.pendingChange.instanceId;
          });
        }
      });
      this.selectedDefaultInstance = null;
      this.showConfirmationModal = false;
      this.pendingChange = null;
    },

    onCategoryInstanceChange(event, category) {
      if (!this.selectedCategoryInstances[category.id]) return;
      
      const selectedValue = this.selectedCategoryInstances[category.id];
      const newInstanceId = selectedValue === this.defaultInstanceId ? null : selectedValue;
      
      this.confirmationType = "category";
      this.confirmationMessage = `Change instance for all roles in category "${category.name}"?`;
      this.pendingChange = {
        type: "category",
        categoryId: category.id,
        instanceId: newInstanceId
      };
      this.showConfirmationModal = true;
    },

    confirmChangeCategoryAllRoles() {
      if (!this.categories || !Array.isArray(this.categories)) {
        return;
      }
      const category = this.categories.find(c => c && c.id === this.pendingChange.categoryId);
      if (category && category.roles && Array.isArray(category.roles)) {
        category.roles.forEach(role => {
          const key = `${category.id}-${role.id}`;
          this.roleInstanceMap[key] = this.pendingChange.instanceId;
        });
      }
      // Reset the category dropdown selection
      this.selectedCategoryInstances[this.pendingChange.categoryId] = null;
      this.showConfirmationModal = false;
      this.pendingChange = null;
      // Force update to reflect changes
      this.$forceUpdate();
    },

    onRoleInstanceChange(event, category, role) {
      const instanceId = event.target.value === this.defaultInstanceId ? null : event.target.value;
      const key = `${category.id}-${role.id}`;
      this.roleInstanceMap[key] = instanceId;
    },

    cancelConfirmation() {
      // Reset dropdowns if needed
      if (this.confirmationType === "defaultInstance") {
        this.selectedDefaultInstance = null;
      } else if (this.confirmationType === "category" && this.pendingChange) {
        this.selectedCategoryInstances[this.pendingChange.categoryId] = null;
      }
      this.showConfirmationModal = false;
      this.pendingChange = null;
      this.confirmationType = null;
      // Force update to revert any UI changes
      this.$forceUpdate();
    },

    handleClickOutside(event) {
      if (this.$refs.clinicDropdown && !this.$refs.clinicDropdown.contains(event.target)) {
        this.clinicDropdownOpen = false;
      }
    },

    async saveMapping() {
      if (!this.selectedClinic) return;

      this.isSaving = true;
      try {
        const clinicLinkId = this.selectedClinic.clinicLinkId;
        const mappings = [];
        
        // Build mappings array - only include non-default instances
        if (!this.categories || !Array.isArray(this.categories)) {
          return;
        }
        this.categories.forEach(category => {
          if (category && category.roles && Array.isArray(category.roles)) {
            category.roles.forEach(role => {
              const key = `${category.id}-${role.id}`;
              const currentInstanceId = this.roleInstanceMap[key] || null;
              
              // Normalize: if instanceId is defaultInstanceId or null, treat as default (null)
              const currentNormalized = (!currentInstanceId || currentInstanceId === this.defaultInstanceId) ? null : currentInstanceId;
              
              // Only add to mappings if it's a non-default instance
              if (currentNormalized !== null) {
                mappings.push({
                  clinicLinkId: clinicLinkId,
                  roleId: role.id,
                  categoryId: category.id,
                  instanceId: currentInstanceId
                });
              }
            });
          }
        });
        
        const requestData = {
          clinicLinkId: clinicLinkId,
          mappings: mappings
        };

        await this.saveInstanceMap(requestData);
        alert("Instance mapping saved successfully!");
        // Update original state to current state after successful save
        this.originalRoleInstanceMap = { ...this.roleInstanceMap };
        // Reload the table
        await this.loadAllRoleInstances();
        this.closeModal();
      } catch (error) {
        console.error("Error saving instance map:", error);
        alert("Failed to save instance mapping. Please try again.");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.phrase-library-container {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.page-header h1 {
  margin: 0;
  color: #003e58;
  font-size: 2rem;
  font-weight: bold;
}

.edit-data-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.edit-data-btn:hover {
  background: #0056b3;
}

.edit-data-btn i {
  font-size: 16px;
}

.content-area {
  flex: 1;
  display: flex;
  justify-content: center;
  overflow: hidden;
  min-height: 0;
  margin-bottom: 40px;
}

.welcome-text {
  font-size: 1.2rem;
  color: #666;
}

.table-section {
  width: 100%;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.table-section h2 {
  color: #003e58;
  margin-bottom: 20px;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.empty-message {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
  flex-shrink: 0;
}

.table-container {
  overflow-x: auto;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  max-height: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding-bottom: 1px;
}

.role-instances-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.role-instances-table thead {
  background: #f5f5f5;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.role-instances-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
  background: #f5f5f5;
}

.role-instances-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  color: #666;
}

.role-instances-table tbody tr:hover {
  background: #f9f9f9;
}

.role-instances-table tbody tr:last-child td {
  border-bottom: none;
}

.changed-instance {
  color: #007bff;
  font-weight: 500;
}

.default-label {
  color: #999;
  font-style: italic;
}

.loading-message {
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 16px;
}

.loading-message i {
  margin-right: 10px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.mapping-modal {
  background: white;
  border-radius: 8px;
  max-width: 900px;
  width: 90%;
  min-height: 600px;
  max-height: 90vh;
  height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  color: #003e58;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close-btn:hover {
  color: #000;
}

.modal-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: visible;
  padding: 30px;
  min-height: 0;
  position: relative;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.clinic-dropdown {
  position: relative;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  background: white;
  z-index: 100;
}

.clinic-selected {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.clinic-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #007bff;
  border-radius: 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1001;
  margin-top: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.clinic-search-container {
  position: sticky;
  top: 0;
  padding: 10px;
  border-bottom: 1px solid #e0e0e0;
  background: #f9f9f9;
  z-index: 1;
}

.clinic-search-input {
  width: 100%;
  padding: 8px 30px 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.clinic-search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.clinic-search-icon {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  pointer-events: none;
}

.clinic-no-results {
  padding: 12px 10px;
  text-align: center;
  color: #999;
  font-style: italic;
}

.clinic-option {
  padding: 12px 10px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.clinic-option:last-child {
  border-bottom: none;
}

.clinic-option:hover {
  background: #e3f2fd;
  color: #007bff;
}

.default-instance-display {
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
  font-weight: 500;
}

.instance-select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.instance-select.highlighted {
  border-color: #ff9800;
  background-color: #fff3e0;
}

.categories-section {
  margin-top: 30px;
}

.categories-section h3 {
  margin-bottom: 20px;
  color: #003e58;
}

.category-item {
  margin-bottom: 25px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.category-name {
  font-weight: 600;
  font-size: 16px;
  color: #333;
}

.category-instance {
  width: 250px;
}

.roles-list {
  margin-left: 20px;
}

.role-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.role-item:last-child {
  border-bottom: none;
}

.role-name {
  flex: 1;
  color: #666;
}

.role-instance {
  width: 250px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px 30px;
  border-top: 1px solid #e0e0e0;
  background: white;
  position: sticky;
  bottom: 0;
  z-index: 10;
  flex-shrink: 0;
}

.cancel-btn,
.save-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.cancel-btn {
  background: #f5f5f5;
  color: #333;
}

.cancel-btn:hover {
  background: #e0e0e0;
}

.save-btn {
  background: #007bff;
  color: white;
}

.save-btn:hover:not(:disabled) {
  background: #0056b3;
}

.save-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Confirmation Modal */
.confirmation-modal {
  background: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
}

.confirmation-modal h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #003e58;
}

.confirmation-modal p {
  margin: 20px 0;
  color: #333;
  font-size: 16px;
}

.confirmation-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.confirm-btn {
  padding: 10px 20px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.confirm-btn:hover {
  background: #0056b3;
}
</style>
